import { PropsWithChildren, ReactNode, RefCallback, useCallback, useMemo } from 'react';

import {
  Box,
  Divider,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  MenuItem,
  Select as MuiSelect,
  SelectProps,
} from '@mui/material';
import { grey } from 'common/theme/palette';

import { FormFieldHint } from '../FormFieldHint';
import { OptionItem, SelectableOptionItem, isOptionGroupLabel, isSelectableOptionItem } from './types';

export type SelectInputProps<T> = Pick<
  SelectProps<T>,
  | 'label'
  | 'placeholder'
  | 'displayEmpty'
  | 'disabled'
  | 'size'
  | 'onChange'
  | 'startAdornment'
  | 'variant'
  | 'sx'
  | 'MenuProps'
  | 'autoWidth'
> & {
  name?: string;
  options: OptionItem<T>[];
  value: T;
  shouldShowError?: boolean;
  'data-testid'?: string;
  error?: { message?: string };
  selectRef?: RefCallback<HTMLSelectElement>;
  renderValue?(selected: T): ReactNode;
};

export const Select = <T = string,>({
  name,
  label,
  options,
  placeholder,
  displayEmpty,
  error,
  selectRef,
  value,
  onChange,
  disabled,
  size,
  shouldShowError = true,
  startAdornment,
  variant,
  sx,
  MenuProps,
  renderValue,
  'data-testid': dataTestId,
}: PropsWithChildren<SelectInputProps<T>>) => {
  const selectableOptions: SelectableOptionItem<T>[] = useMemo(() => options.filter(isSelectableOptionItem), [options]);

  const selectedLabel = useCallback(
    (selected: T) => selectableOptions.find((option) => option.value === selected)?.label,
    [selectableOptions]
  );
  const selectedValueIcon = selectableOptions.find((option) => option.value === value)?.Icon;
  const defaultRenderValue = (selected: T) =>
    typeof selected === 'string' && selected?.length === 0 ? placeholder : selectedLabel(selected);

  return (
    <FormControl error={!!error} fullWidth>
      {!displayEmpty && (
        <InputLabel
          id={`${name}-label`}
          size={size === 'small' ? 'small' : 'normal'}
          sx={{ '&.Mui-error': { color: grey[400] } }}
        >
          {label}
        </InputLabel>
      )}
      <MuiSelect
        label={displayEmpty ? undefined : label}
        startAdornment={startAdornment ?? selectedValueIcon}
        data-testid={dataTestId}
        fullWidth
        displayEmpty={displayEmpty}
        labelId={`${name}-label`}
        ref={selectRef}
        value={value}
        renderValue={renderValue ?? defaultRenderValue}
        onChange={onChange}
        error={!!error}
        disabled={disabled}
        size={size}
        placeholder={placeholder}
        variant={variant}
        sx={sx}
        MenuProps={MenuProps}
      >
        {options.map((option, index) =>
          isSelectableOptionItem(option) ? (
            <MenuItem
              key={index}
              value={option.value as string}
              disabled={option.value === undefined || option.value === null || option.disabled}
              data-testid={`menu-item-${option.value}`}
            >
              {option?.Icon && <ListItemIcon sx={{ color: 'inherit' }}>{option?.Icon}</ListItemIcon>}
              {option?.label && <ListItemText>{option.label}</ListItemText>}
            </MenuItem>
          ) : isOptionGroupLabel(option) ? (
            <Box key={index}>
              {index > 0 && <Divider sx={{ mb: 1 }} />}
              <ListSubheader>{option.label}</ListSubheader>
            </Box>
          ) : (
            <Box key={index}>{option.component}</Box>
          )
        )}
      </MuiSelect>
      {shouldShowError && (
        <FormFieldHint error sx={{ mt: 0.5 }}>
          {error?.message}
        </FormFieldHint>
      )}
    </FormControl>
  );
};
